import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Car, Shield, Droplet, Package, User, Clock, Loader2, X,
  Camera, AlertTriangle, ChevronLeft, ChevronRight, Plus, Minus,
  Download, Upload, Save, PenLine, CheckCircle2, Trash2, FileDown, Maximize2,
  Palette
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { resolveVehicleModel } from "../../utils/vehicleDisplay";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { API } from "../../App";
import { WashIncludedBadge } from "../WashPerk";
import {
  UsagePurposeBadge, UsagePurposePicker, noteRequiredFor, purposeMeta,
} from "./UsagePurpose";
import { StaffPaymentPanel } from "./StaffPaymentPanel";
import JobProgressTracker from "./JobProgressTracker";
import { ComplaintRedoDialog, ComplaintHistory } from "./ComplaintRedoDialog";
import { CorrectDepositDialog, DepositCorrectionHistory } from "./CorrectDepositDialog";
import { generateJobCardPDF } from "../../utils/jobCardPdf";

const formatQuantity = (amount, baseUnit) => {
  if (baseUnit === "cm") {
    const meters = Math.floor(amount / 100);
    const cm = Math.round(amount % 100);
    if (meters > 0) return cm > 0 ? `${meters}m ${cm}cm` : `${meters}m`;
    return `${cm}cm`;
  }
  if (baseUnit === "ml") return `${Math.round(amount)}ml`;
  return `${Math.round(amount)} pcs`;
};

const getStepSize = (baseUnit) => {
  if (baseUnit === "cm") return 10;
  if (baseUnit === "ml") return 50;
  return 1;
};

const photoUrl = (photoId) => `${API}/jobs/photos/${photoId}`;

// SEC-002: authenticated <img> — fetches the photo with the session token in the
// Authorization header (via axios interceptor) and renders it from a blob URL,
// so the token never ends up in the browser URL/history/logs.
const AuthImage = React.memo(function AuthImage({ photoId, alt, className }) {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    if (!photoId) return undefined;
    let cancelled = false;
    let objectUrl = null;
    axios
      .get(`${API}/jobs/photos/${photoId}`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photoId]);
  if (!src) return <div className={`${className || ""} bg-slate-800 animate-pulse`} data-testid="auth-image-loading" />;
  return <img src={src} alt={alt} className={className} />;
});

const DAMAGE_LOCATIONS = [
  "Front bumper", "Rear bumper", "Hood/Bonnet", "Roof",
  "Driver door", "Passenger door", "Driver rear door", "Passenger rear door",
  "Driver fender", "Passenger fender", "Driver mirror", "Passenger mirror",
  "Windshield", "Rear window", "Driver side windows", "Passenger side windows",
  "Trunk/Boot", "Wheels/Rims", "Other"
];

const JobCardViewModal = ({ isOpen, onClose, booking, alertContext, onAcknowledge, isAdmin = false, permissions = {} }) => {
  const [jobLog, setJobLog] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [photoType, setPhotoType] = useState("before"); // "before" | "after" | "damage"
  const [selectedDamageIdx, setSelectedDamageIdx] = useState(0);

  // Inline materials editor state
  const [editMaterials, setEditMaterials] = useState([]);
  const [floatUnits, setFloatUnits] = useState([]);
  const [unitsByCategory, setUnitsByCategory] = useState({});
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [isSavingMaterials, setIsSavingMaterials] = useState(false);

  // Notes editor
  const [notesDraft, setNotesDraft] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Warranty editor
  const [warrantyDraft, setWarrantyDraft] = useState("");
  const [isSavingWarranty, setIsSavingWarranty] = useState(false);

  // Working photos upload (during in_progress: quick photo dump into after photos)
  const workPhotoInput = useRef(null);
  const [uploadingWorkPhoto, setUploadingWorkPhoto] = useState(false);

  // After photos during QC
  const qcPhotoInput = useRef(null);
  const [uploadingQcPhoto, setUploadingQcPhoto] = useState(false);

  // Damage-during-work form
  const [showDamageForm, setShowDamageForm] = useState(false);
  const [dmgDescription, setDmgDescription] = useState("");
  const [dmgLocation, setDmgLocation] = useState("");
  const [dmgPhotos, setDmgPhotos] = useState([]); // [{preview, uploading, photo_id}]
  const dmgPhotoInput = useRef(null);
  const [dmgCustomerName, setDmgCustomerName] = useState("");
  const [dmgSignature, setDmgSignature] = useState(null);
  const [isSavingDamage, setIsSavingDamage] = useState(false);
  const dmgSigCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // PDF export state
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    if (isOpen && booking?.booking_id) {
      loadJobLog();
    }
    if (!isOpen) {
      setPaidOverride(null);
      // reset transient state
      setShowDamageForm(false);
      setDmgDescription("");
      setDmgLocation("");
      setDmgPhotos([]);
      setDmgSignature(null);
      setDmgCustomerName("");
    }
  }, [isOpen, booking?.booking_id]);

  // Survives the loadJobLog() re-render that unmounts the payment panel.
  const [paidOverride, setPaidOverride] = useState(null);

  const initMaterialsEditor = (log, allUnits) => {
    const rows = (log?.materials_used || []).filter(m => m.unit_id).map(m => {
      const fu = allUnits.find(u => u.unit_id === m.unit_id);
      const amount = parseFloat(m.amount_used) || 0;
      return {
        unit_id: m.unit_id,
        product_name: m.product_name || m.item_name || "Material",
        base_unit: m.base_unit || "pcs",
        amount_used: amount,
        max_allowed: amount + (fu?.remaining_quantity || 0),
        // Carry the saved purpose through so a staff re-save never
        // silently reverts an admin's reclassification.
        usage_purpose: m.usage_purpose || "customer_job",
        usage_note: m.usage_note || "",
      };
    });
    setEditMaterials(rows);
  };

  const loadJobLog = async () => {
    setIsLoading(true);
    let log = null;
    try {
      const response = await axios.get(`${API}/jobs/log/${booking.booking_id}`);
      log = response.data;
    } catch {
      try {
        const legacyResponse = await axios.get(`${API}/job-logs/booking/${booking.booking_id}`);
        log = legacyResponse.data;
      } catch {
        log = null;
      }
    }
    setJobLog(log);
    setNotesDraft(log?.notes || "");
    setWrapFinish(booking?.wrap_finish || log?.wrap_finish || "");
    setWarrantyDraft(log?.warranty_duration || booking?.warranty_duration || "");
    setDmgCustomerName(booking?.customer_name || "");
    if (log && log.stage !== "completed") {
      try {
        const floatRes = await axios.get(`${API}/stock/items/for-job`);
        const byCat = floatRes.data.items_by_category || {};
        const all = [];
        Object.values(byCat).forEach(units => all.push(...units));
        setUnitsByCategory(byCat);
        setCategories(floatRes.data.categories || []);
        setFloatUnits(all);
        initMaterialsEditor(log, all);
      } catch {
        initMaterialsEditor(log, []);
      }
    }
    setIsLoading(false);
  };

  const savedRows = (jobLog?.materials_used || []).filter(m => m.unit_id);
  const legacyRows = (jobLog?.materials_used || []).filter(m => !m.unit_id);
  const materialsChanged =
    editMaterials.length !== savedRows.length ||
    editMaterials.some(r => {
      const s = savedRows.find(m => m.unit_id === r.unit_id);
      return !s || Math.abs((parseFloat(s.amount_used) || 0) - r.amount_used) > 0.001;
    });

  const notesChanged = (notesDraft || "") !== (jobLog?.notes || "");
  const warrantyChanged = (warrantyDraft || "") !== (jobLog?.warranty_duration || booking?.warranty_duration || "");

  const handleAddUnit = () => {
    const unit = floatUnits.find(u => u.unit_id === selectedUnit);
    if (!unit) return;
    if (editMaterials.some(m => m.unit_id === unit.unit_id)) {
      toast.info("This unit is already in the list");
      return;
    }
    setEditMaterials(prev => [...prev, {
      unit_id: unit.unit_id,
      product_name: unit.name,
      base_unit: unit.base_unit,
      amount_used: Math.min(getStepSize(unit.base_unit), unit.remaining_quantity),
      max_allowed: unit.remaining_quantity,
      usage_purpose: "customer_job",
      usage_note: "",
    }]);
    setSelectedUnit("");
  };

  const handleAmountChange = (unitId, newAmount) => {
    setEditMaterials(prev => prev.map(m =>
      m.unit_id === unitId
        ? { ...m, amount_used: Math.min(Math.max(0, newAmount), m.max_allowed) }
        : m
    ));
  };

  // Metadata only — never moves stock. Staff set it when logging.
  const handlePurposeChange = (unitId, purpose, note) => {
    setEditMaterials(prev => prev.map(m =>
      m.unit_id === unitId ? { ...m, usage_purpose: purpose, usage_note: note } : m
    ));
  };

  const saveMaterials = async () => {
    setIsSavingMaterials(true);
    try {
      const missingNote = editMaterials.find(
        m => m.amount_used > 0 && noteRequiredFor(m.usage_purpose)
          && !(m.usage_note || "").trim()
      );
      if (missingNote) {
        toast.error(`Add a note for "${missingNote.product_name}" — say what the ${purposeMeta(missingNote.usage_purpose).short.toLowerCase()} was for`);
        setIsSavingMaterials(false);
        return;
      }
      const payload = editMaterials
        .filter(m => m.amount_used > 0)
        .map(m => ({
          unit_id: m.unit_id,
          amount_used: m.amount_used,
          usage_purpose: m.usage_purpose || "customer_job",
          usage_note: (m.usage_note || "").trim() || null,
        }));
      await axios.put(`${API}/jobs/materials/${booking.booking_id}`, { materials_used: payload });
      toast.success("Materials saved");
      await loadJobLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save materials");
    } finally {
      setIsSavingMaterials(false);
    }
  };

  const saveNotes = async () => {
    setIsSavingNotes(true);
    try {
      await axios.patch(`${API}/jobs/notes/${booking.booking_id}`, { notes: notesDraft || "" });
      toast.success("Notes saved");
      await loadJobLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save notes");
    } finally {
      setIsSavingNotes(false);
    }
  };

  const saveWarranty = async () => {
    if (!warrantyDraft) {
      toast.error("Please select a warranty duration");
      return;
    }
    setIsSavingWarranty(true);
    try {
      await axios.patch(`${API}/jobs/warranty/${booking.booking_id}`, { warranty_duration: warrantyDraft });
      toast.success("Warranty saved");
      await loadJobLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save warranty");
    } finally {
      setIsSavingWarranty(false);
    }
  };

  // Upload photo helper
  const uploadPhoto = async (file, photoType) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(
      `${API}/jobs/photos/upload?booking_id=${booking.booking_id}&photo_type=${photoType}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data.photo_id;
  };

  const handleWorkPhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingWorkPhoto(true);
    const uploaded = [];
    for (const file of files) {
      if (file.type && !file.type.startsWith("image/")) {
        // An MPO/HEIC from the phone gallery can arrive with an EMPTY or
        // non-standard MIME type, so a client-side type check silently
        // dropped real photos (Pav, Oct 2026). Only reject what is clearly
        // not an image, and SAY SO. The server validates the real bytes.
        toast.error(`${file.name} is not an image — use the Camera button`);
        continue;
      }
      try {
        const pid = await uploadPhoto(file, "after");
        uploaded.push(pid);
      } catch (error) {
        toast.error(error.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    if (uploaded.length > 0) {
      try {
        await axios.post(`${API}/jobs/add-after-photos/${booking.booking_id}`, uploaded);
        toast.success(`${uploaded.length} photo(s) added`);
        await loadJobLog();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to attach photos to job");
      }
    }
    if (workPhotoInput.current) workPhotoInput.current.value = "";
    if (qcPhotoInput.current) qcPhotoInput.current.value = "";
    setUploadingWorkPhoto(false);
    setUploadingQcPhoto(false);
  };

  const handleQcPhotoSelect = async (e) => {
    setUploadingQcPhoto(true);
    await handleWorkPhotoSelect(e);
  };

  // Damage form photo handling
  const handleDamagePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    for (const file of files) {
      if (file.type && !file.type.startsWith("image/")) {
        // An MPO/HEIC from the phone gallery can arrive with an EMPTY or
        // non-standard MIME type, so a client-side type check silently
        // dropped real photos (Pav, Oct 2026). Only reject what is clearly
        // not an image, and SAY SO. The server validates the real bytes.
        toast.error(`${file.name} is not an image — use the Camera button`);
        continue;
      }
      const preview = URL.createObjectURL(file);
      const entry = { preview, uploading: true, photo_id: null };
      setDmgPhotos(prev => [...prev, entry]);
      try {
        const pid = await uploadPhoto(file, "damage");
        setDmgPhotos(prev => prev.map(p => p.preview === preview ? { ...p, uploading: false, photo_id: pid } : p));
      } catch {
        setDmgPhotos(prev => prev.filter(p => p.preview !== preview));
        toast.error("Failed to upload damage photo");
      }
    }
    if (dmgPhotoInput.current) dmgPhotoInput.current.value = "";
  };

  // Signature drawing
  const getCanvasCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };
  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = dmgSigCanvasRef.current;
    if (!canvas) return;
    setIsDrawing(true);
    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoordinates(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const stopDrawing = () => setIsDrawing(false);
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = dmgSigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoordinates(e, canvas);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#ff6b35";
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const clearDmgSignature = () => {
    const canvas = dmgSigCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setDmgSignature(null);
  };
  const saveDmgSignature = () => {
    const canvas = dmgSigCanvasRef.current;
    if (canvas) {
      setDmgSignature(canvas.toDataURL("image/png"));
      toast.success("Signature captured");
    }
  };

  const submitDamage = async () => {
    if (!dmgLocation && !dmgDescription.trim()) {
      toast.error("Add description or location");
      return;
    }
    setIsSavingDamage(true);
    try {
      await axios.post(`${API}/jobs/damage/${booking.booking_id}`, {
        description: dmgDescription.trim(),
        location: dmgLocation,
        photo_ids: dmgPhotos.filter(p => p.photo_id).map(p => p.photo_id),
        signature: dmgSignature,
        customer_name: dmgCustomerName || null,
      });
      toast.success("Damage record added");
      setShowDamageForm(false);
      setDmgDescription("");
      setDmgLocation("");
      setDmgPhotos([]);
      setDmgSignature(null);
      await loadJobLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save damage");
    } finally {
      setIsSavingDamage(false);
    }
  };

  const downloadPhoto = useCallback(async (photoId, filename) => {
    try {
      const res = await axios.get(`${API}/jobs/photos/${photoId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `photo-${photoId}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download photo");
    }
  }, []);

  const exportPdf = async (variant = "merchant") => {
    setExportingPdf(true);
    try {
      await generateJobCardPDF({ booking, jobLog, variant, photoUrl });
      toast.success(`${variant === "customer" ? "Customer" : "Merchant"} PDF generated`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const [resendingEmail, setResendingEmail] = useState(false);
  const [emailFixInput, setEmailFixInput] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [showAdminDrawer, setShowAdminDrawer] = useState(false);
  const [showComplaint, setShowComplaint] = useState(false);
  const [complaintRefresh, setComplaintRefresh] = useState(0);
  const resendCollectionEmail = async () => {
    if (!booking?.booking_id) return;
    if (!window.confirm("Resend the 'Your Car is Ready!' email to the customer? If this is a deposit-only job (total ≤ deposit), the VAT invoice will also be finalized and emailed.")) {
      return;
    }
    setResendingEmail(true);
    try {
      const { data } = await axios.post(`${API}/jobs/resend-collection-email/${booking.booking_id}`);
      const target = data.customer_email || "customer";
      let msg = `Collection email resent to ${target}`;
      if (data.invoice_action === "queued") msg += " · invoice being generated";
      toast.success(msg);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Failed to resend email");
    } finally {
      setResendingEmail(false);
    }
  };

  const saveCustomerEmail = async () => {
    const email = (emailFixInput || "").trim();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSavingEmail(true);
    try {
      await axios.put(`${API}/auth/bookings/${booking.booking_id}`, { customer_email: email });
      const { data } = await axios.post(`${API}/jobs/resend-collection-email/${booking.booking_id}`);
      let msg = `Email saved & sent to ${data.customer_email || email}`;
      if (data.invoice_action === "queued") msg += " · invoice being generated";
      toast.success(msg);
      setEmailFixInput("");
      if (booking) booking.customer_email = email;
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Failed to save email");
    } finally {
      setSavingEmail(false);
    }
  };

  // ------- Cash payment + upsell handlers -------
  const [cashLoading, setCashLoading] = useState(false);
  const [generateInvoiceLoading, setGenerateInvoiceLoading] = useState(false);
  const generateInvoice = async () => {
    if (!window.confirm("Generate the VAT invoice for this booking and email it to the customer?")) return;
    setGenerateInvoiceLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/bookings/${booking.booking_id}/generate-invoice`);
      let msg = `Invoice ${data.invoice_number} generated`;
      if (data.invoice_email_sent) msg += " · emailed to customer";
      else if (data.invoice_email_last_error) msg += ` · email FAILED (${data.invoice_email_last_error})`;
      toast.success(msg);
      if (booking) {
        booking.invoice_id = data.invoice_id;
        booking.invoice_number = data.invoice_number;
        booking.invoice_email_sent = data.invoice_email_sent;
        booking.invoice_email_last_error = data.invoice_email_last_error;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate invoice");
    } finally {
      setGenerateInvoiceLoading(false);
    }
  };
  const markCashPaid = async (scope, confirmText) => {
    if (!window.confirm(confirmText)) return;
    setCashLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/bookings/${booking.booking_id}/mark-cash-paid`, { scope });
      let msg = scope === "balance" || scope === "both"
        ? "Marked fully paid (cash)"
        : "Deposit marked paid (cash)";
      if (data.invoice_action === "queued") msg += " · VAT invoice being generated + emailed";
      toast.success(msg);
      // Optimistic UI reflect
      if (booking) {
        if (data.deposit_paid) booking.deposit_paid = true;
        if (data.balance_paid) { booking.balance_paid = true; booking.payment_method = "cash"; }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to mark cash payment");
    } finally {
      setCashLoading(false);
    }
  };

  const [showUpsell, setShowUpsell] = useState(false);
  const [showCorrectDeposit, setShowCorrectDeposit] = useState(false);
  const [wrapFinish, setWrapFinish] = useState("");
  const [savingWrapFinish, setSavingWrapFinish] = useState(false);
  // Mirrors markCashPaid's optimistic pattern: the parent owns the reload,
  // so without a local copy the panel still shows the old EUR 50.
  const [depositFix, setDepositFix] = useState(null);
  const [upsellItems, setUpsellItems] = useState([{ name: "", price: "" }]);
  const [upsellSaving, setUpsellSaving] = useState(false);
  const submitUpsell = async () => {
    const items = upsellItems
      .map((i) => ({ name: (i.name || "").trim(), price: parseFloat(i.price || 0) }))
      .filter((i) => i.name && i.price > 0);
    if (items.length === 0) {
      toast.error("Add at least one line item with a name and price");
      return;
    }
    setUpsellSaving(true);
    try {
      const { data } = await axios.post(`${API}/auth/bookings/${booking.booking_id}/upsell`, {
        custom_line_items: items,
      });
      toast.success(`+€${data.delta.toFixed(2)} added · new total €${data.new_total.toFixed(2)}`);
      if (booking) {
        booking.total_price = data.new_total;
        if (data.new_balance !== undefined) booking.balance_amount = data.new_balance;
      }
      setShowUpsell(false);
      setUpsellItems([{ name: "", price: "" }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upsell failed");
    } finally {
      setUpsellSaving(false);
    }
  };

  if (!booking) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString('en-IE', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const beforePhotos = jobLog?.before_photos || [];
  const afterPhotos = jobLog?.after_photos || [];

  const saveWrapFinish = async (value) => {
    setSavingWrapFinish(true);
    const next = value === "auto" ? null : value;
    try {
      await axios.post(`${API}/auth/bookings/${booking.booking_id}/wrap-finish`,
                       { finish: next });
      setWrapFinish(next || "");
      if (booking) booking.wrap_finish = next || undefined;
      toast.success(next
        ? `Wrap finish set to ${next} — the aftercare email will match`
        : "Wrap finish cleared — back to auto-detect");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the wrap finish");
    } finally {
      setSavingWrapFinish(false);
    }
  };

  const damageRecords = jobLog?.damage_records || [];
  const stage = jobLog?.stage || "checked_in";
  const isCompleted = stage === "completed";
  const canEdit = !isCompleted && jobLog;

  // Matte/satin film can't be polished and rubbing burnishes it, so the
  // generic gloss wrap aftercare is actively wrong for it. The finish is
  // often absent from the material name, hence the manual override.
  const hasWrapWork = [
    ...(jobLog?.service_names || []),
    ...(booking?.services_details || []).map((s) => s?.name),
    ...(jobLog?.materials_used || []).map((m) => m?.product_name),
  ].some((t) => /wrap|chrome delete|chrome-delete|debadge/i.test(String(t || "")));

  const currentPhotos =
    photoType === "before" ? beforePhotos :
    photoType === "after" ? afterPhotos :
    (damageRecords[selectedDamageIdx]?.photos || []);

  const openPhotoViewer = (index, type, damageIdx = 0) => {
    setPhotoType(type);
    setSelectedDamageIdx(damageIdx);
    setSelectedPhotoIndex(index);
  };
  const closePhotoViewer = () => setSelectedPhotoIndex(null);
  const nextPhoto = () => { if (selectedPhotoIndex < currentPhotos.length - 1) setSelectedPhotoIndex(selectedPhotoIndex + 1); };
  const prevPhoto = () => { if (selectedPhotoIndex > 0) setSelectedPhotoIndex(selectedPhotoIndex - 1); };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white w-[calc(100vw-1rem)] sm:w-full max-w-2xl h-[90dvh] sm:h-auto sm:max-h-[90vh] p-0 overflow-hidden flex flex-col overscroll-contain">
          {/* Header — fixed, does not scroll. */}
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-slate-800">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <FileText className="w-6 h-6 text-saturn-orange" />
              Job Card
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Job details for {booking.customer_name}
              {booking.wash_perk?.entitled && (
                <span className="ml-2 inline-block align-middle">
                  <WashIncludedBadge />
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Body — the ONLY scrollable region. `flex-1` gives it the
              remaining height between header and footer. `min-h-0` is
              required inside a flex column or the child grows to fit
              its content instead of scrolling (a classic flexbox gotcha
              that was the reason the Close button was hiding under the
              mobile browser chrome). */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-saturn-orange" />
            </div>
          ) : (
            <div className="space-y-4">
              {alertContext && (
                <div
                  className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-4 flex items-start gap-3"
                  data-testid="alert-context-banner"
                >
                  <div className="flex-shrink-0 mt-0.5 text-amber-400 text-xl">⚠️</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-amber-300 font-semibold text-sm">
                      {alertContext.kind === "duration"
                        ? "Duration anomaly"
                        : "Materials cost anomaly"} flagged
                    </div>
                    <div className="text-slate-300 text-sm mt-1 tabular-nums">
                      Actual{" "}
                      <span className="text-white font-medium">
                        {alertContext.kind === "duration"
                          ? `${alertContext.actual} min`
                          : `€${Number(alertContext.actual).toFixed(2)}`}
                      </span>{" "}
                      · Combo median{" "}
                      <span className="text-white">
                        {alertContext.kind === "duration"
                          ? `${alertContext.median} min`
                          : `€${Number(alertContext.median).toFixed(2)}`}
                      </span>{" "}
                      · Threshold{" "}
                      <span className="text-white">
                        {alertContext.kind === "duration"
                          ? `${alertContext.threshold} min`
                          : `€${Number(alertContext.threshold).toFixed(2)}`}
                      </span>{" "}
                      ({alertContext.multiplier}× median · n={alertContext.sample_size})
                    </div>
                  </div>
                  {!alertContext.acknowledged && onAcknowledge && (
                    <button
                      onClick={() => onAcknowledge(alertContext)}
                      className="flex-shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded px-3 py-1.5"
                      data-testid="alert-context-acknowledge-btn"
                    >
                      ✓ Acknowledge
                    </button>
                  )}
                  {alertContext.acknowledged && (
                    <span
                      className="flex-shrink-0 text-emerald-400 text-sm"
                      data-testid="alert-context-acknowledged"
                    >
                      ✓ Acknowledged
                    </span>
                  )}
                </div>
              )}

              {/* Job Progress */}
              {jobLog && (
                <JobProgressTracker
                  bookingId={booking.booking_id}
                  currentStage={stage}
                  compact={false}
                />
              )}

              {/* Admin Drawer — collapsible zone for payment / PDFs / resend / missing-email fix.
                    Keeps the stage-driven work area (photos, materials, damage) clutter-free.
                    Missing-email warning auto-expands the drawer when completed jobs lack an email.

                    ADMIN ONLY (6 Oct 2026). Everything in here is money or
                    a money ACTION: the toggle label quotes the balance due,
                    the panel quotes total/deposit/balance, and the buttons
                    take cash, raise an invoice, run an upsell and export the
                    merchant PDF. Staff keep photos, materials, damage and
                    stage — all of which live OUTSIDE this div. */}
              {/* Staff with `see_payment_amounts` get a read-mostly Payment
                  panel instead of the admin drawer. Admins keep the drawer,
                  which already contains all of this and much more. */}
              {/* Complaint / redo — admin only, on a COMPLETED job. A redo
                  visit itself never offers this: a complaint always belongs
                  on the original job. */}
              {isAdmin && isCompleted && !booking?.is_redo && (
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowComplaint(true)}
                    className="w-full border-amber-600/50 text-amber-300 hover:bg-amber-600/10"
                    data-testid="log-complaint-btn"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Log complaint / schedule redo
                  </Button>
                  <ComplaintHistory bookingId={booking.booking_id}
                                    refreshKey={complaintRefresh} />
                </div>
              )}

              {/* A redo visit carries no money at all — no payment panel for
                  staff OR admin, nothing to collect, nothing to invoice. */}
              {!isAdmin && permissions.see_payment_amounts && !booking?.is_redo && (
                <StaffPaymentPanel
                  booking={booking}
                  canTakeCash={!!permissions.take_cash_payments}
                  paidOverride={paidOverride}
                  onPaid={(res) => {
                    setPaidOverride({ deposit_paid: res.deposit_paid,
                                      balance_paid: res.balance_paid });
                    loadJobLog();
                  }}
                />
              )}

              {isAdmin && !booking?.is_redo && (
              <div className="bg-slate-900/40 border border-slate-700 rounded-lg overflow-hidden" data-testid="admin-drawer">
                <button
                  type="button"
                  onClick={() => setShowAdminDrawer(!showAdminDrawer)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-900/60 transition-colors"
                  data-testid="admin-drawer-toggle"
                >
                  <div className="flex items-center gap-3 text-sm">
                    <Shield className="w-4 h-4 text-orange-400" />
                    <span className="text-white font-semibold">Admin Actions</span>
                    <span className="text-slate-400">
                      · {booking?.deposit_paid ? "Deposit ✓" : "Deposit unpaid"} · {booking?.balance_paid ? "Balance ✓" : `€${(Number(booking?.total_price || 0) - Number(booking?.deposit_amount || 0)).toFixed(2)} due`}
                    </span>
                    {isCompleted && !booking?.customer_email && (
                      <span className="text-xs bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded">⚠ Email missing</span>
                    )}
                  </div>
                  <span className="text-slate-400 text-lg">{showAdminDrawer ? "−" : "+"}</span>
                </button>
                {(showAdminDrawer || (isCompleted && !booking?.customer_email)) && (
                  <div className="p-4 space-y-4 border-t border-slate-700">
                    {/* Missing customer email warning */}
                    {isCompleted && !booking?.customer_email && (
                      <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-lg p-4" data-testid="missing-email-warning">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 text-sm text-yellow-100">
                            <p className="font-semibold mb-1">No customer email on file</p>
                            <p className="text-yellow-200/80 mb-3">The &ldquo;Your Car is Ready&rdquo; email and VAT invoice can&apos;t be sent to this customer because no email address was saved on the booking. Add one below, then re-send.</p>
                            <div className="flex flex-wrap gap-2 items-center">
                              <input
                                type="email"
                                value={emailFixInput}
                                onChange={(e) => setEmailFixInput(e.target.value)}
                                placeholder="customer@example.com"
                                className="flex-1 min-w-[200px] px-3 py-2 rounded bg-slate-900 border border-yellow-500/40 text-white text-sm placeholder:text-slate-500"
                                data-testid="missing-email-input"
                              />
                              <Button size="sm" onClick={saveCustomerEmail} disabled={savingEmail || !emailFixInput.trim()} className="bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-semibold" data-testid="missing-email-save-btn">
                                {savingEmail ? "Saving…" : "Save & Send Email"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Payment status + cash / upsell actions */}
                    <div className="bg-slate-900/40 border border-slate-700 rounded-lg p-4 space-y-3" data-testid="payment-actions-panel">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-slate-400">Deposit:</span>
                  <span className={booking?.deposit_paid ? "text-emerald-400 font-semibold" : "text-yellow-400 font-semibold"}
                        data-testid="payment-panel-deposit">
                    {booking?.deposit_paid ? `Paid${booking?.payment_source_deposit === "cash" ? " (cash)" : ""}` : "Unpaid"}
                    {" "}
                    <span className="text-slate-300 font-normal">
                      €{Number(depositFix?.deposit_amount ?? booking?.deposit_amount ?? 50).toFixed(2)}
                    </span>
                  </span>
                  <span className="text-slate-600">·</span>
                  <span className="text-slate-400">Balance:</span>
                  <span className={booking?.balance_paid ? "text-emerald-400 font-semibold" : "text-yellow-400 font-semibold"}
                        data-testid="payment-panel-balance">
                    {booking?.balance_paid
                      ? `Paid${booking?.payment_source_balance === "cash" ? " (cash)" : ""}`
                      : `€${(
                          // Read the STORED discounted balance. NEVER
                          // recompute `total - deposit` here — that
                          // ignores any promo/wheel/jackpot discount and
                          // overcharges cash-paying customers (Lukas
                          // Jaki bug, Sep 2026). Precedence mirrors the
                          // backend mark_cash_paid guard.
                          Number(
                            depositFix?.balance_due ??
                            booking?.balance_amount ??
                            booking?.remaining_balance_due ??
                            (Number(booking?.total_price || 0)
                              - Number(booking?.deposit_amount || 0)
                              - Number(booking?.promo_discount_applied || 0))
                          )
                        ).toFixed(2)} due`}
                  </span>
                  <span className="ml-auto text-slate-400">Total: <strong className="text-white">€{Number(booking?.total_price || 0).toFixed(2)}</strong></span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {!booking?.balance_paid && !booking?.invoice_id && (
                    // The booking flow assumes a flat €50. When the
                    // customer paid something else (usually a Revolut link
                    // sent by hand on a big job) the €50 is simply wrong and
                    // every balance downstream is too high by the
                    // difference. REPLACES the figure; money arriving after
                    // this is a balance payment, not another deposit.
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowCorrectDeposit(true)}
                      className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-950/30"
                      data-testid="correct-deposit-open-btn"
                      title="The customer paid a different deposit than the standard €50"
                    >
                      Correct deposit received
                    </Button>
                  )}
                  {!booking?.deposit_paid && (
                    <Button
                      size="sm"
                      onClick={() => markCashPaid("deposit", `Confirm deposit paid in cash for €${Number(booking?.deposit_amount || 50).toFixed(2)}?`)}
                      disabled={cashLoading}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="mark-deposit-cash-btn"
                    >
                      Deposit paid (cash)
                    </Button>
                  )}
                  {booking?.deposit_paid && !booking?.balance_paid && (
                    <Button
                      size="sm"
                      onClick={() => {
                        // Read stored discounted balance — see the
                        // "€X due" span above for the same precedence.
                        // Never bare (total - deposit).
                        const dueAmt = Number(
                          booking?.balance_amount ??
                          booking?.remaining_balance_due ??
                          (Number(booking?.total_price || 0)
                            - Number(booking?.deposit_amount || 0)
                            - Number(booking?.promo_discount_applied || 0))
                        );
                        markCashPaid(
                          "balance",
                          `Confirm balance paid in cash for €${dueAmt.toFixed(2)}? This auto-generates + emails the VAT invoice.`
                        );
                      }}
                      disabled={cashLoading}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="mark-balance-cash-btn"
                    >
                      Balance paid (cash)
                    </Button>
                  )}
                  {booking?.invoice_error && (
                    // The invoice pipeline fails silently from the
                    // payment webhook / cash-paid task, so without this
                    // the admin sees a paid job with no invoice and no
                    // reason. `invoice_error` is cleared automatically
                    // once an invoice is generated successfully.
                    <div
                      className="w-full text-xs rounded border border-red-500/40 bg-red-950/30 text-red-200 px-3 py-2"
                      data-testid="invoice-error-banner"
                    >
                      <span className="font-semibold">Invoice not generated:</span>{" "}
                      {booking.invoice_error}
                    </div>
                  )}

                  {booking?.balance_paid && !booking?.invoice_id && (
                    <Button
                      size="sm"
                      onClick={generateInvoice}
                      disabled={generateInvoiceLoading}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="generate-invoice-btn"
                      title="Balance is paid but no VAT invoice exists — click to generate and email it now (recovery from the missed auto-trigger)."
                    >
                      {generateInvoiceLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
                      Generate Invoice
                    </Button>
                  )}
                  {!booking?.balance_paid && !booking?.invoice_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowUpsell(true)}
                      className="border-orange-500/50 text-orange-300 hover:bg-orange-950/30"
                      data-testid="upsell-open-btn"
                    >
                      + Add Service / Line Item
                    </Button>
                  )}
                  {booking?.balance_paid && (
                    <span className="text-xs text-slate-500 italic">Upsell disabled — booking is fully paid</span>
                  )}
                </div>
                <DepositCorrectionHistory
                  corrections={depositFix?.correction
                    ? [...(booking?.deposit_corrections || []).filter(
                        (c) => c.correction_id !== depositFix.correction.correction_id),
                       depositFix.correction]
                    : booking?.deposit_corrections}
                />
              </div>

              {/* PDF download / view actions for completed */}
              {isCompleted && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => exportPdf("merchant")}
                    disabled={exportingPdf}
                    className="bg-saturn-orange hover:bg-saturn-orange/80"
                    data-testid="download-pdf-merchant-btn"
                  >
                    {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                    Merchant PDF
                  </Button>
                  <Button
                    onClick={() => exportPdf("customer")}
                    disabled={exportingPdf}
                    variant="outline"
                    className="border-slate-600 text-slate-200 hover:bg-slate-800"
                    data-testid="download-pdf-customer-btn"
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Customer PDF
                  </Button>
                  <Button
                    onClick={resendCollectionEmail}
                    disabled={resendingEmail}
                    variant="outline"
                    className="border-emerald-700 text-emerald-300 hover:bg-emerald-900/40"
                    data-testid="resend-collection-email-btn"
                    title="Re-send the 'Your Car is Ready!' email + auto-finalize invoice for deposit-only jobs"
                  >
                    {resendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Resend Customer Email
                  </Button>
                </div>
              )}
                  </div>
                )}
              </div>
              )}

              {/* Wrap finish — decides WHICH aftercare the customer is sent.
                  Matte/satin film can't be polished and rubbing leaves a
                  permanent shiny patch, so the gloss advice is wrong for it.
                  Auto-detected from the film name; this override exists
                  because the finish often isn't recorded there. */}
              {isAdmin && hasWrapWork && (
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-2"
                     data-testid="wrap-finish-panel">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <Palette className="w-4 h-4 text-saturn-orange" />
                    Wrap finish
                  </h4>
                  <p className="text-xs text-slate-400">
                    Satin and matte film gets different aftercare — it can't be
                    polished, and rubbing leaves a permanent shiny patch.
                  </p>
                  <Select
                    value={wrapFinish || "auto"}
                    onValueChange={saveWrapFinish}
                    disabled={savingWrapFinish}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white w-full sm:w-64"
                                   data-testid="wrap-finish-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700 text-white">
                      <SelectItem value="auto">Auto-detect from the film used</SelectItem>
                      <SelectItem value="gloss">Gloss</SelectItem>
                      <SelectItem value="satin">Satin</SelectItem>
                      <SelectItem value="matte">Matte</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Customer & Vehicle Info */}
              <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-white flex items-center gap-2">
                  <Car className="w-4 h-4 text-saturn-orange" />
                  Vehicle & Customer
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-400 block">Customer</span>
                    <span className="text-white font-medium">{booking.customer_name}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Phone</span>
                    <span className="text-white">{booking.customer_phone || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Vehicle</span>
                    <span className="text-white">
                      {booking.vehicle?.year} {booking.vehicle?.make} {resolveVehicleModel(booking)}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Registration</span>
                    <span className="text-white font-mono font-bold text-saturn-orange">
                      {booking.vehicle_reg || booking.vehicle?.reg || jobLog?.vehicle_reg || "Not recorded"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Before/After Photos */}
              {(beforePhotos.length > 0 || afterPhotos.length > 0) && (
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-3 min-w-0">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <Camera className="w-4 h-4 text-blue-400" />
                    Photos
                  </h4>

                  {beforePhotos.length > 0 && (
                    <div className="min-w-0">
                      <span className="text-slate-400 text-sm block mb-2">Before ({beforePhotos.length})</span>
                      <div className="flex gap-2 overflow-x-auto pb-2 max-w-full">
                        {beforePhotos.map((photo, idx) => (
                          <button
                            key={photo.photo_id || idx}
                            onClick={() => openPhotoViewer(idx, "before")}
                            className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-slate-700 hover:ring-2 hover:ring-saturn-orange transition-all"
                            data-testid={`before-photo-thumb-${idx}`}
                          >
                            <AuthImage photoId={photo.photo_id} alt={`Before ${idx + 1}`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {afterPhotos.length > 0 && (
                    <div className="min-w-0">
                      <span className="text-slate-400 text-sm block mb-2">After ({afterPhotos.length})</span>
                      <div className="flex gap-2 overflow-x-auto pb-2 max-w-full">
                        {afterPhotos.map((photo, idx) => (
                          <button
                            key={photo.photo_id || idx}
                            onClick={() => openPhotoViewer(idx, "after")}
                            className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-slate-700 hover:ring-2 hover:ring-saturn-orange transition-all"
                            data-testid={`after-photo-thumb-${idx}`}
                          >
                            <AuthImage photoId={photo.photo_id} alt={`After ${idx + 1}`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Add photos during In Progress (quick dump) */}
              {/* Photo capture — stage-aware:
                    • Checked In → "Before Photos" (required to proceed)
                    • In Progress → "Working Photos" (optional progress shots)
                    • Quality Check → "After Photos" (required to complete) */}
              {canEdit && (stage === "checked_in" || stage === "in_progress" || stage === "qc") && (
                <div className={`rounded-lg p-4 space-y-2 border ${stage === "qc" ? "bg-green-500/10 border-green-500/30" : stage === "checked_in" ? "bg-blue-500/10 border-blue-500/30" : "bg-slate-800/50 border-slate-700"}`} data-testid={stage === "qc" ? "qc-photos-section" : "work-photos-section"}>
                  <Label className="text-white flex items-center gap-2">
                    <Camera className={`w-4 h-4 ${stage === "qc" ? "text-green-400" : stage === "checked_in" ? "text-blue-400" : "text-slate-400"}`} />
                    {stage === "checked_in" ? "Before Photos" : stage === "qc" ? "After Photos" : "Working Photos"}
                    {(stage === "checked_in" || stage === "qc") && <span className="text-xs text-yellow-400">· required</span>}
                  </Label>
                  <p className="text-slate-500 text-xs">
                    {stage === "checked_in"
                      ? "Capture the as-received condition of the vehicle before starting work."
                      : stage === "qc"
                      ? "Capture the finished work before completing the job."
                      : "Optional — snap progress shots or issues as you work."}
                  </p>
                  <div className="flex gap-2">
                    <label className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border-2 border-dashed cursor-pointer text-sm ${stage === "qc" ? "border-green-500/50 bg-green-500/10 hover:bg-green-500/20 text-green-400" : "border-blue-500/50 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400"}`}>
                      {(uploadingWorkPhoto || uploadingQcPhoto) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                      Camera
                      <input
                        ref={stage === "qc" ? qcPhotoInput : workPhotoInput}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={stage === "qc" ? handleQcPhotoSelect : handleWorkPhotoSelect}
                        className="hidden"
                        data-testid={stage === "qc" ? "qc-photo-camera" : "work-photo-camera"}
                      />
                    </label>
                    <label className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border-2 border-dashed border-slate-600 cursor-pointer hover:border-slate-500 text-slate-400 text-sm">
                      <Upload className="w-4 h-4" />
                      Gallery
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={stage === "qc" ? handleQcPhotoSelect : handleWorkPhotoSelect}
                        className="hidden"
                        data-testid={stage === "qc" ? "qc-photo-gallery" : "work-photo-gallery"}
                      />
                    </label>
                  </div>
                </div>
              )}

              {/* Damage Records */}
              {damageRecords.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-yellow-400 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Damage Records
                  </h4>
                  <div className="space-y-2">
                    {damageRecords.map((damage, idx) => (
                      <div key={damage.damage_id || idx} className="bg-slate-900/50 rounded p-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <span className="text-yellow-300 font-medium">{damage.location}</span>
                            {damage.recorded_during_work && (
                              <Badge className="ml-2 bg-orange-500/20 text-orange-300 text-xs">During work</Badge>
                            )}
                            <p className="text-slate-300 text-sm mt-1">{damage.description}</p>
                          </div>
                          {damage.acknowledged_by_customer && (
                            <Badge className="bg-green-500/20 text-green-400 text-xs">Signed</Badge>
                          )}
                        </div>
                        {damage.photos?.length > 0 && (
                          <div className="flex gap-1 mt-2 flex-wrap">
                            {damage.photos.map((p, i) => (
                              <button
                                key={i}
                                onClick={() => openPhotoViewer(i, "damage", idx)}
                                className="w-12 h-12 rounded overflow-hidden hover:ring-2 hover:ring-yellow-400"
                              >
                                <AuthImage photoId={p.photo_id} alt={`Damage ${i + 1}`} className="w-full h-full object-cover" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {jobLog?.damage_sign_off && (
                    <div className="border-t border-yellow-500/30 pt-3 mt-3">
                      <p className="text-sm text-slate-400">
                        Acknowledged by: <span className="text-white">{jobLog.damage_sign_off.customer_name}</span>
                      </p>
                      <p className="text-xs text-slate-500">Signed at: {formatDate(jobLog.damage_sign_off.signed_at)}</p>
                      {jobLog.damage_sign_off.signature && (
                        <img src={jobLog.damage_sign_off.signature} alt="Customer Signature" className="mt-2 h-16 bg-white rounded" />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Add Damage during work */}
              {canEdit && (stage === "in_progress" || stage === "qc") && (
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-3" data-testid="add-damage-section">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-white flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-400" />
                      Log Damage / Issue
                    </h4>
                    {!showDamageForm && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowDamageForm(true)}
                        className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                        data-testid="log-damage-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Log Damage
                      </Button>
                    )}
                  </div>

                  {showDamageForm && (
                    <div className="space-y-3">
                      <select
                        value={dmgLocation}
                        onChange={(e) => setDmgLocation(e.target.value)}
                        className="w-full bg-slate-800 border border-slate-700 rounded-md p-2 text-white text-sm"
                        data-testid="dmg-location"
                      >
                        <option value="">Select location...</option>
                        {DAMAGE_LOCATIONS.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                      </select>
                      <Textarea
                        placeholder="Describe what happened / observed damage..."
                        value={dmgDescription}
                        onChange={(e) => setDmgDescription(e.target.value)}
                        className="bg-slate-800 border-slate-700 text-white text-sm"
                        data-testid="dmg-description"
                      />
                      <div className="flex gap-2 flex-wrap">
                        {dmgPhotos.map((p, idx) => (
                          <div key={idx} className="relative w-14 h-14">
                            <img src={p.preview} alt={`dmg ${idx}`} className="w-full h-full object-cover rounded" />
                            {p.uploading && (
                              <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded">
                                <Loader2 className="w-4 h-4 animate-spin" />
                              </div>
                            )}
                          </div>
                        ))}
                        <label className="w-14 h-14 rounded border-2 border-dashed border-yellow-500/50 bg-yellow-500/10 flex flex-col items-center justify-center cursor-pointer">
                          <Camera className="w-4 h-4 text-yellow-500" />
                          <span className="text-[10px] text-yellow-500">Camera</span>
                          <input
                            ref={dmgPhotoInput}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={handleDamagePhotoSelect}
                            className="hidden"
                            data-testid="dmg-photo-camera"
                          />
                        </label>
                        <label className="w-14 h-14 rounded border-2 border-dashed border-slate-600 flex flex-col items-center justify-center cursor-pointer">
                          <Upload className="w-4 h-4 text-slate-500" />
                          <span className="text-[10px] text-slate-500">Gallery</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleDamagePhotoSelect}
                            className="hidden"
                            data-testid="dmg-photo-gallery"
                          />
                        </label>
                      </div>

                      {/* Optional signature */}
                      <div className="border-t border-slate-700 pt-3 space-y-2">
                        <Label className="text-slate-300 text-xs flex items-center gap-1">
                          <PenLine className="w-3 h-3" /> Customer sign-off (optional)
                        </Label>
                        <Input
                          value={dmgCustomerName}
                          onChange={(e) => setDmgCustomerName(e.target.value)}
                          placeholder="Customer name"
                          className="bg-slate-800 border-slate-700 text-white text-sm"
                          data-testid="dmg-customer-name"
                        />
                        <div className="bg-slate-800 rounded p-2">
                          <canvas
                            ref={dmgSigCanvasRef}
                            width={400}
                            height={110}
                            className="w-full bg-white rounded cursor-crosshair touch-none"
                            style={{ touchAction: 'none' }}
                            onMouseDown={startDrawing}
                            onMouseUp={stopDrawing}
                            onMouseMove={draw}
                            onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchEnd={stopDrawing}
                            onTouchMove={draw}
                            onTouchCancel={stopDrawing}
                            data-testid="dmg-signature-canvas"
                          />
                          <div className="flex gap-2 mt-1">
                            <Button size="sm" variant="outline" onClick={clearDmgSignature} className="h-7 text-xs border-slate-600">Clear</Button>
                            <Button size="sm" onClick={saveDmgSignature} className="h-7 text-xs bg-green-600 hover:bg-green-700" data-testid="dmg-save-signature">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Save Sig
                            </Button>
                            {dmgSignature && <span className="text-xs text-green-400 self-center">Captured</span>}
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={submitDamage}
                          disabled={isSavingDamage}
                          className="bg-yellow-600 hover:bg-yellow-700"
                          data-testid="dmg-submit-btn"
                        >
                          {isSavingDamage ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                          Save Damage
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-slate-600"
                          onClick={() => {
                            setShowDamageForm(false);
                            setDmgDescription("");
                            setDmgLocation("");
                            setDmgPhotos([]);
                            setDmgSignature(null);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Warranty Info — editable in In Progress; read-only pill in QC/Completed once set */}
              <div className="bg-slate-800/50 rounded-lg p-4 space-y-3" data-testid="warranty-section">
                <h4 className="font-medium text-white flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-500" />
                  Warranty {canEdit && stage === "in_progress" && !jobLog?.warranty_duration && <span className="text-red-400 text-xs">*required to complete</span>}
                </h4>
                {canEdit && stage === "in_progress" ? (
                  <div className="space-y-2">
                    <Select value={warrantyDraft} onValueChange={setWarrantyDraft}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="warranty-select">
                        <SelectValue placeholder="Select warranty period..." />
                      </SelectTrigger>
                      <SelectContent>
                        {["Lifetime", "10 Years", "7 Years", "5 Years", "3 Years", "1 Year", "None"].map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={saveWarranty}
                      disabled={!warrantyChanged || isSavingWarranty}
                      className="bg-saturn-orange hover:bg-saturn-orange/80 disabled:opacity-40"
                      data-testid="save-warranty-btn"
                    >
                      {isSavingWarranty ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : warrantyChanged ? "Save Warranty" : "Warranty Saved"}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <Badge className={`text-base px-3 py-1 ${
                      (booking.warranty_duration || jobLog?.warranty_duration) === 'Lifetime'
                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                        : (booking.warranty_duration || jobLog?.warranty_duration)
                        ? 'bg-saturn-orange/20 text-saturn-orange border-saturn-orange/30'
                        : 'bg-slate-700 text-slate-400'
                    }`}>
                      {booking.warranty_duration || jobLog?.warranty_duration || "Not specified"}
                    </Badge>
                    {(booking.vehicle_reg || jobLog?.vehicle_reg) && (
                      <span className="text-slate-400 text-sm">
                        Linked to reg: <span className="text-white font-mono">{booking.vehicle_reg || jobLog?.vehicle_reg}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Services & Tint Shade */}
              <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-white flex items-center gap-2">
                  <Droplet className="w-4 h-4 text-blue-400" />
                  Services Performed
                </h4>
                <div className="space-y-2">
                  {(() => {
                    const names = (jobLog?.service_names?.length ? jobLog.service_names : null)
                      || booking.services_details?.map(s => s.name).filter(Boolean)
                      || [];
                    return names.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {names.map((service, i) => (
                          <Badge key={i} className="bg-saturn-orange/20 text-saturn-orange">
                            {service}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">No services recorded.</p>
                    );
                  })()}
                  {jobLog?.shade_variant && (
                    <div className="mt-2 pt-2 border-t border-slate-700">
                      <span className="text-slate-400 text-sm">Tint Shade Applied: </span>
                      <span className="text-white font-medium">{jobLog.shade_variant}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Materials Used — full editor in In Progress; read-only summary in QC/Completed */}
              {canEdit && stage === "in_progress" ? (
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-3" data-testid="materials-editor">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <Package className="w-4 h-4 text-purple-400" />
                    Materials Used
                  </h4>

                  {legacyRows.map((mat, i) => (
                    <div key={`legacy-${i}`} className="flex justify-between items-center text-sm bg-slate-900/50 rounded px-3 py-2">
                      <span className="text-white">{mat.item_name || mat.product_name || "Material"}</span>
                      <span className="text-slate-400">{mat.quantity !== undefined ? `${mat.quantity} ${mat.unit || 'units'}` : 'N/A'}</span>
                    </div>
                  ))}

                  {editMaterials.length === 0 && legacyRows.length === 0 && (
                    <p className="text-slate-500 text-sm">No materials logged yet — add them as you use them.</p>
                  )}

                  {editMaterials.map((material) => (
                    <div key={material.unit_id} className="bg-slate-900/50 rounded px-3 py-2 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-white truncate">{material.product_name}</span>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 flex-shrink-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          onClick={() => setEditMaterials(prev => prev.filter(m => m.unit_id !== material.unit_id))}
                          data-testid={`material-remove-${material.unit_id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-slate-400 hover:text-white bg-slate-800"
                          onClick={() => handleAmountChange(material.unit_id, material.amount_used - getStepSize(material.base_unit))}
                          data-testid={`material-decrease-${material.unit_id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <Input
                          type="number"
                          step={getStepSize(material.base_unit)}
                          min="0"
                          max={material.max_allowed}
                          value={material.amount_used}
                          onChange={(e) => handleAmountChange(material.unit_id, parseFloat(e.target.value) || 0)}
                          className="w-24 h-8 text-center bg-slate-800 border-slate-700 text-white text-sm"
                          data-testid={`material-amount-${material.unit_id}`}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-slate-400 hover:text-white bg-slate-800"
                          onClick={() => handleAmountChange(material.unit_id, material.amount_used + getStepSize(material.base_unit))}
                          data-testid={`material-increase-${material.unit_id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                        <span className="text-sm text-saturn-orange font-medium ml-auto whitespace-nowrap">
                          {formatQuantity(material.amount_used, material.base_unit)}
                        </span>
                      </div>
                      <div className="pt-1 border-t border-slate-700/60">
                        <span className="text-[11px] text-slate-400 block mb-1">
                          What was this used for?
                        </span>
                        <UsagePurposePicker
                          purpose={material.usage_purpose || "customer_job"}
                          note={material.usage_note || ""}
                          onChange={(purpose, note) =>
                            handlePurposeChange(material.unit_id, purpose, note)}
                          idSuffix={`-${material.unit_id}`}
                          compact
                        />
                      </div>
                    </div>
                  ))}

                  <div className="flex flex-wrap gap-2 pt-1">
                    <Select value={selectedCategory} onValueChange={(val) => { setSelectedCategory(val); setSelectedUnit(""); }}>
                      <SelectTrigger className="flex-1 min-w-[130px] h-9 bg-slate-800 border-slate-700 text-white text-sm" data-testid="material-category-select">
                        <SelectValue placeholder="Category..." />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={selectedUnit} onValueChange={setSelectedUnit} disabled={!selectedCategory}>
                      <SelectTrigger className="flex-[2] min-w-[160px] h-9 bg-slate-800 border-slate-700 text-white text-sm" data-testid="material-unit-select">
                        <SelectValue placeholder="Item..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {(unitsByCategory[selectedCategory] || []).map(unit => (
                          <SelectItem
                            key={unit.unit_id}
                            value={unit.unit_id}
                            disabled={editMaterials.some(m => m.unit_id === unit.unit_id)}
                          >
                            {unit.name} • {unit.remaining_display}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={handleAddUnit}
                      disabled={!selectedUnit}
                      className="h-9 bg-green-600 hover:bg-green-700 disabled:opacity-50"
                      data-testid="material-add-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  <Button
                    className="w-full bg-saturn-orange hover:bg-saturn-orange/80 disabled:opacity-40"
                    disabled={!materialsChanged || isSavingMaterials}
                    onClick={saveMaterials}
                    data-testid="save-materials-btn"
                  >
                    {isSavingMaterials ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                    ) : materialsChanged ? "Save Materials" : "Materials Saved"}
                  </Button>
                </div>
              ) : (
                jobLog?.materials_used?.length > 0 && (
                  <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                    <h4 className="font-medium text-white flex items-center gap-2">
                      <Package className="w-4 h-4 text-purple-400" />
                      Materials Used
                    </h4>
                    <div className="space-y-2">
                      {jobLog.materials_used.map((mat, i) => {
                        const name = mat.product_name || mat.item_name || "Unknown material";
                        const amount = mat.amount_display ||
                          (mat.amount_used ? `${mat.amount_used} ${mat.base_unit || 'units'}` :
                          (mat.quantity !== undefined ? `${mat.quantity} ${mat.unit || 'units'}` : 'N/A'));
                        const cost = typeof mat.material_cost === "number"
                          ? `€${mat.material_cost.toFixed(2)}` : null;
                        return (
                          <div
                            key={i}
                            className="flex justify-between items-center text-sm bg-slate-900/50 rounded px-3 py-2"
                            data-testid={`material-row-${i}`}
                          >
                            <span className="text-white flex items-center gap-2">
                              {name}
                              <UsagePurposeBadge
                                purpose={mat.usage_purpose}
                                note={mat.usage_note}
                                testId={`material-purpose-${i}`}
                              />
                            </span>
                            <span className="flex items-center gap-3">
                              <span className="text-saturn-orange font-medium">{amount}</span>
                              {cost && (
                                <span
                                  className="text-emerald-400 font-medium tabular-nums"
                                  data-testid={`material-cost-${i}`}
                                  title="Proportional cost based on amount used"
                                >
                                  {cost}
                                </span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                      {/* Total materials cost — only shown if at least one row has a cost */}
                      {(() => {
                        const withCost = (jobLog.materials_used || []).filter(m => typeof m.material_cost === "number");
                        if (withCost.length === 0) return null;
                        const total = withCost.reduce((s, m) => s + m.material_cost, 0);
                        const missing = (jobLog.materials_used || []).length - withCost.length;
                        return (
                          <div
                            className="flex justify-between items-center text-sm border-t border-slate-700/50 pt-2 mt-1"
                            data-testid="materials-cost-total"
                          >
                            <span className="text-slate-300">
                              Materials cost
                              {missing > 0 && (
                                <span className="text-slate-500 text-xs ml-1">
                                  ({missing} item{missing === 1 ? "" : "s"} without cost data)
                                </span>
                              )}
                            </span>
                            <span className="text-emerald-400 font-semibold tabular-nums">
                              €{total.toFixed(2)}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )
              )}

              {/* Progress Notes (editable during active stages) */}
              {canEdit && (
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-2" data-testid="notes-editor">
                  <Label className="text-white flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-400" />
                    Progress Notes
                  </Label>
                  <p className="text-slate-500 text-xs">
                    Log tint shade / wrap colour, issues encountered, or any observations.
                    <span className="block mt-1 text-amber-500/80">
                      Please avoid pasting customer PII — plate numbers live in the Reg field, addresses shouldn't be here at all.
                    </span>
                  </p>
                  <Textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    placeholder="e.g. 20% VLT applied on all rear windows, minor scuff noted on driver's B-pillar during install."
                    className="bg-slate-900 border-slate-700 text-white min-h-[80px]"
                    data-testid="notes-textarea"
                  />
                  <Button
                    size="sm"
                    onClick={saveNotes}
                    disabled={!notesChanged || isSavingNotes}
                    className="bg-saturn-orange hover:bg-saturn-orange/80 disabled:opacity-40"
                    data-testid="save-notes-btn"
                  >
                    {isSavingNotes ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : notesChanged ? "Save Notes" : "Notes Saved"}
                  </Button>
                </div>
              )}

              {/* Technician & Timing */}
              <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-white flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  {isCompleted ? "Completion Details" : "Job Details"}
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-400 block">Technician</span>
                    <span className="text-white">{jobLog?.technician_name || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Duration</span>
                    <span className="text-white">
                      {jobLog?.duration_minutes ? `${Math.floor(jobLog.duration_minutes / 60)}h ${jobLog.duration_minutes % 60}m` : "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Booking Date</span>
                    <span className="text-white">{booking.booking_date} at {booking.booking_time}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Completed</span>
                    <span className="text-white">{formatDate(jobLog?.end_time || booking.completed_at)}</span>
                  </div>
                </div>
              </div>

              {/* Notes readonly view for completed */}
              {isCompleted && jobLog?.notes && (
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-2">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-400" />
                    Notes
                  </h4>
                  <p className="text-slate-300 text-sm whitespace-pre-wrap">{jobLog.notes}</p>
                </div>
              )}

              {!jobLog && !isLoading && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
                  <p className="text-yellow-400 text-sm">
                    No detailed job log found for this booking. This may be an older booking completed before job tracking was enabled.
                  </p>
                </div>
              )}
            </div>
          )}
          </div>

          {/* Footer — pinned. `shrink-0` prevents flexbox from squashing
              it when the body overflows. Always visible regardless of
              scroll position, viewport height, or mobile URL-bar chrome. */}
          <div className="shrink-0 border-t border-slate-800 px-6 py-3 flex justify-end bg-slate-900">
            <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300" data-testid="close-job-card-btn">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Viewer Modal */}
      {selectedPhotoIndex !== null && currentPhotos.length > 0 && (
        <Dialog open={true} onOpenChange={closePhotoViewer}>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-4xl p-0 overflow-hidden">
            <div className="relative">
              <AuthImage
                photoId={currentPhotos[selectedPhotoIndex]?.photo_id}
                alt={`Photo ${selectedPhotoIndex + 1}`}
                className="w-full max-h-[80vh] object-contain bg-black"
              />

              {currentPhotos.length > 1 && (
                <>
                  <button
                    onClick={prevPhoto}
                    disabled={selectedPhotoIndex === 0}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white disabled:opacity-30"
                    data-testid="photo-prev-btn"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextPhoto}
                    disabled={selectedPhotoIndex === currentPhotos.length - 1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white disabled:opacity-30"
                    data-testid="photo-next-btn"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <div className="flex items-center justify-between text-white gap-2">
                  <span className="text-sm">
                    {photoType === "before" ? "Before" : photoType === "after" ? "After" : "Damage"} Photo {selectedPhotoIndex + 1} of {currentPhotos.length}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadPhoto(
                        currentPhotos[selectedPhotoIndex]?.photo_id,
                        currentPhotos[selectedPhotoIndex]?.original_filename
                      )}
                      className="border-white/30 text-white hover:bg-white/10"
                      data-testid="photo-download-btn"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={closePhotoViewer}
                      className="border-white/30 text-white hover:bg-white/10"
                      data-testid="photo-close-btn"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Upsell dialog — add extra services / one-off line items mid-job.
          `open` is also isAdmin-gated so it can never be opened by a
          staff session even if some future caller sets showUpsell. */}
      <Dialog open={isAdmin && showUpsell} onOpenChange={setShowUpsell}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Add extra line items</DialogTitle>
            <DialogDescription className="text-slate-400">
              Bump the booking total mid-job (e.g. customer approved a sunstrip while their tint was fitted).
              Each line updates the total and shows on the final VAT invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {upsellItems.map((it, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`upsell-row-${i}`}>
                <Input
                  value={it.name}
                  onChange={(e) => {
                    const arr = [...upsellItems]; arr[i] = { ...arr[i], name: e.target.value }; setUpsellItems(arr);
                  }}
                  placeholder="Item name (e.g. Sunstrip fit)"
                  className="bg-slate-800 border-slate-700 flex-1"
                />
                <div className="relative w-32">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">€</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={it.price}
                    onChange={(e) => {
                      const arr = [...upsellItems]; arr[i] = { ...arr[i], price: e.target.value }; setUpsellItems(arr);
                    }}
                    placeholder="0.00"
                    className="bg-slate-800 border-slate-700 pl-7"
                  />
                </div>
                {upsellItems.length > 1 && (
                  <button
                    onClick={() => setUpsellItems(upsellItems.filter((_, j) => j !== i))}
                    className="text-red-400 hover:text-red-300 text-lg px-1"
                    aria-label="Remove"
                  >×</button>
                )}
              </div>
            ))}
            <button
              onClick={() => setUpsellItems([...upsellItems, { name: "", price: "" }])}
              className="text-orange-400 hover:text-orange-300 text-sm"
              data-testid="upsell-add-row-btn"
            >+ another line</button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpsell(false)} className="border-slate-700">Cancel</Button>
            <Button onClick={submitUpsell} disabled={upsellSaving} className="bg-orange-500 hover:bg-orange-600" data-testid="upsell-save-btn">
              {upsellSaving ? "Adding…" : "Add to Booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isAdmin && (
        <ComplaintRedoDialog
          open={showComplaint}
          onClose={() => setShowComplaint(false)}
          booking={booking}
          onSaved={() => setComplaintRefresh((n) => n + 1)}
        />
      )}

      {/* `open` is isAdmin-gated for the same reason the upsell dialog is:
          the endpoint refuses a staff token, but the dialog must not be
          reachable at all. */}
      {isAdmin && (
        <CorrectDepositDialog
          open={showCorrectDeposit}
          onClose={() => setShowCorrectDeposit(false)}
          booking={booking}
          onSaved={(data) => {
            setDepositFix(data);
            if (booking) {
              booking.deposit_amount = data.deposit_amount;
              booking.deposit_paid = true;
              booking.balance_amount = data.balance_due;
              booking.remaining_balance_due = data.balance_due;
              booking.payment_source_deposit = data.method;
            }
          }}
        />
      )}
    </>
  );
};

export default JobCardViewModal;
